import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from '../routing';
import { dishesApi, formatCurrency, formatDishCurrency } from '../utils/api';
import { toast } from '../components/Toast';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import EmptyState from '../components/EmptyState';
import InlineLoadError from '../components/InlineLoadError';
import ListRow from '../components/ListRow';
import { useIsMobile } from '../utils/useIsMobile';
import { useConfirm } from '../utils/useConfirm';
import { track, EVENTS } from '../utils/analytics';
import { useAuth } from '../context/AuthContext';
import HqOnlyNotice from '../components/HqOnlyNotice';
import PageViewAsMenu from '../components/ViewAsMenu';

// "עץ מוצר" — list of dishes with live ingredient cost vs menu price.
const fmtPct = (p) => (p == null ? '-' : `${(p * 100).toFixed(1)}%`);

// Food cost is the share of the menu price eaten by ingredients. <33% healthy,
// 33–40% watch, >40% (or no menu price) flagged.
//
// ⚠ These thresholds are ONE restaurant's norms wearing a universal hat. Food
// cost varies enormously by concept — a pizzeria runs ~25%, a steakhouse ~40%,
// a bar ~20% — so a steakhouse sees red on nearly every dish and stops
// believing the badge. Kept as one function, like ALERT_THRESHOLD_PCT on the
// server, so it can become a per-restaurant setting without reshaping callers.
const FOOD_COST_GOOD = 0.33;
const FOOD_COST_WATCH = 0.40;

// An incomplete dish gets NO verdict, whatever the arithmetic says. Its cost is
// understated (unpriced/unconverted ingredients contribute 0), so the % would
// come out low and paint the least-configured dish green — the exact inverse of
// the truth, on the screen people scan to find problems.
function foodCostBadge(pct, incomplete) {
  if (incomplete) return 'badge-gray';
  if (pct == null) return 'badge-gray';
  if (pct <= FOOD_COST_GOOD) return 'badge-green';
  if (pct <= FOOD_COST_WATCH) return 'badge-yellow';
  return 'badge-red';
}

const INCOMPLETE_TITLE = 'לחלק מהמרכיבים חסרה המרה או מחיר, ולכן הם נספרים כ-0. העלות האמיתית גבוהה יותר.';

// Direction colours are the app's semantic pair (index.css / Icon.jsx), NOT the
// retired #e74c3c/#27ae60 — those measured 3.82:1 and 2.87:1 and were dropped
// app-wide for failing AA on exactly this kind of signal.
const UP_COLOR = '#b91c1c';
const DOWN_COLOR = '#15803d';

function DirMark({ dir, children }) {
  return (
    <span style={{ color: dir === 'up' ? UP_COLOR : DOWN_COLOR, fontWeight: 700, whiteSpace: 'nowrap' }}>
      <Icon name="chevron" size={12} style={dir === 'up' ? { transform: 'rotate(180deg)' } : undefined} />
      {children ? <> {children}</> : null}
    </span>
  );
}

export default function DishesPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { restaurantId } = useAuth();
  const [askConfirm, confirmDialog] = useConfirm();
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed fetch must never fall through to the empty state: "עדיין אין מנות"
  // and "the server is unreachable" are different facts, and rendering the first
  // when the second is true invites someone to re-create dishes they already
  // have. Same rule (and the same reason) as DraftsPage/UnpricedItemsPage.
  const [loadError, setLoadError] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addPrice, setAddPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [alerts, setAlerts] = useState([]);
  // Which row's ⋮ menu is open (dish id), or null. One at a time.
  const [menuFor, setMenuFor] = useState(null);
  const hqDishes = dishes.filter(d => d.hidden_hq_cost > 0).length;
  const [showHelp, setShowHelp] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, aRes] = await Promise.all([dishesApi.getAll(), dishesApi.getAlerts()]);
      setDishes(Array.isArray(dRes.data) ? dRes.data : []);
      setAlerts(Array.isArray(aRes.data?.dishes) ? aRes.data.dishes : []);
      setLoadError(false);
    } catch {
      setDishes([]);
      setAlerts([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // dish id → 'up' | 'down' (net cost direction) for the row marker.
  const alertDir = new Map(alerts.map(a => [a.id, a.new_cost >= a.old_cost ? 'up' : 'down']));

  const markViewed = async () => {
    try {
      await dishesApi.markAlertsViewed();
      track(EVENTS.DISH_PRICE_ALERTS_REVIEWED, {
        restaurant_id: String(restaurantId),
        dish_count: alerts.length,
      });
      setShowAlerts(false);
      toast('ההתראות סומנו כנקראו');
      window.dispatchEvent(new Event('dish-alerts-changed'));
      load();
    } catch { toast('שגיאה בעדכון ההתראות', 'error'); }
  };

  useEffect(() => { load(); }, [load]);

  // One per visit to עץ מוצר — the module's adoption metric. It rolls out one
  // restaurant at a time and is invisible to viewers, so the question is who
  // opened it and from which restaurant.
  //
  // The person (id, email, role) and the tenant (restaurant_id, restaurant_name)
  // are already super properties, set by identifyUser/setRestaurantGroup in
  // AuthContext — restaurant_id is repeated in the payload anyway so the event
  // is readable on its own, without relying on a registration that happens in
  // another module. Keyed on restaurantId so switching tenant on this screen
  // counts as a new visit; it is the unit of adoption here.
  useEffect(() => {
    if (restaurantId == null) return;
    track(EVENTS.DISH_LIST_VIEWED, { restaurant_id: String(restaurantId) });
  }, [restaurantId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addName.trim()) { toast('שם המנה הוא שדה חובה', 'error'); return; }
    setSaving(true);
    try {
      const res = await dishesApi.create({ name: addName.trim(), menu_price: addPrice });
      track(EVENTS.DISH_CREATED, {
        restaurant_id: String(restaurantId),
        // Whether a price was set up front — a dish created without one cannot
        // produce a food-cost % until someone comes back to it.
        with_menu_price: !!(parseFloat(addPrice) > 0),
      });
      toast('המנה נוצרה');
      setShowAdd(false);
      setAddName(''); setAddPrice('');
      navigate(`/dishes/${res.data.id}`);
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה ביצירת המנה', 'error');
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (dish) => askConfirm({
    title: 'מחיקת מנה',
    message: `למחוק את המנה "${dish.name}"?`,
    detail: dish.ingredient_count > 0 ? `${dish.ingredient_count} מרכיבים יימחקו יחד איתה.` : undefined,
    confirmLabel: 'מחק את המנה',
    tone: 'danger',
    onConfirm: async () => {
      try {
        await dishesApi.delete(dish.id);
        toast('המנה נמחקה');
        load();
      } catch {
        toast('שגיאה במחיקת המנה', 'error');
      }
    },
  });

  // An EMPTY dish is unmeasured too. Its cost is 0 because nothing has been
  // added yet, so the food-cost percentage came out 0.0% and painted green —
  // the most flattering verdict in the app, on the one dish we know nothing
  // about. Same failure as the unconverted ingredient, one corner further out.
  const unmeasured = (d) => d.incomplete_count > 0 || d.ingredient_count === 0;
  const verdictText = (d) => {
    if (d.ingredient_count === 0) return 'אין מרכיבים';
    if (d.incomplete_count > 0) return 'חלקי';
    return fmtPct(d.food_cost_pct);
  };

  const incompleteNote = (d) => (d.incomplete_count > 0
    ? <span className="badge badge-yellow" title={INCOMPLETE_TITLE}>{d.incomplete_count} ללא המרה</span>
    : null);

  const body = () => {
    if (loading) {
      return <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>;
    }
    if (loadError) {
      return <InlineLoadError message="שגיאה בטעינת המנות" onRetry={load} />;
    }
    if (dishes.length === 0) {
      return <EmptyState message="עדיין אין מנות. צרו מנה ראשונה כדי לחשב את עלות המרכיבים שלה." />;
    }
    if (isMobile) {
      // Row taps through to the dish, which owns delete — the same division as
      // DocumentsPage's mobile list. ListRow is a <button>, so a per-row delete
      // could not be nested inside it anyway.
      return (
        <div className="m-list">
          {dishes.map(d => (
            <ListRow
              key={d.id}
              onClick={() => navigate(`/dishes/${d.id}`)}
              primary={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  {alertDir.has(d.id) && <DirMark dir={alertDir.get(d.id)} />}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                </span>
              }
              meta={<span>{d.ingredient_count} מרכיבים</span>}
              badge={
                <>
                  <span className={`badge ${foodCostBadge(d.food_cost_pct, unmeasured(d))}`}>
                    {verdictText(d)}
                  </span>
                  {incompleteNote(d)}
                </>
              }
              amount={formatDishCurrency(d.total_cost)}
            />
          ))}
        </div>
      );
    }
    return (
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>שם המנה</th>
              <th>מרכיבים</th>
              <th>עלות (ברוטו)</th>
              <th>מחיר מכירה</th>
              <th title="כמה אחוז ממחיר המכירה הולך לחומרי הגלם (עלות ÷ מחיר מכירה). נמוך = רווחי יותר.">עלות מזון %</th>
              <th>רווח גולמי</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {dishes.map(d => (
              <tr key={d.id} className="row-clickable" onClick={() => navigate(`/dishes/${d.id}`)}>
                <td>
                  {alertDir.has(d.id) && (
                    <span style={{ marginInlineEnd: 6 }}
                      title={alertDir.get(d.id) === 'up' ? 'מרכיב התייקר' : 'מרכיב הוזל'}>
                      <DirMark dir={alertDir.get(d.id)} />
                    </span>
                  )}
                  {d.name}
                </td>
                <td>
                  {d.ingredient_count}
                  {d.incomplete_count > 0 && <> {incompleteNote(d)}</>}
                </td>
                <td>{formatDishCurrency(d.total_cost)}</td>
                <td>{d.menu_price > 0 ? formatCurrency(d.menu_price) : '-'}</td>
                <td>
                  <span className={`badge ${foodCostBadge(d.food_cost_pct, unmeasured(d))}`}
                    title={d.incomplete_count > 0 ? INCOMPLETE_TITLE : undefined}>
                    {verdictText(d)}
                  </span>
                </td>
                <td>{unmeasured(d) || d.gross_profit == null ? '-' : formatDishCurrency(d.gross_profit)}</td>
                {/* A ⋮ menu, not a red button at rest. Delete is irreversible
                    and it was the only always-visible control in the row -
                    the heaviest colour in the table sitting on every line,
                    permanently, one stray click from a destructive dialog on a
                    row that is itself clickable. Same vocabulary as the
                    document page's row of actions. */}
                <td onClick={e => e.stopPropagation()}>
                  <div className="page-menu">
                    <button
                      type="button"
                      className="page-menu-trigger"
                      aria-label={`פעולות עבור ${d.name}`}
                      aria-expanded={menuFor === d.id}
                      onClick={() => setMenuFor(cur => (cur === d.id ? null : d.id))}
                    >⋮</button>
                    {menuFor === d.id && (
                      <>
                        <div className="menu-backdrop" onClick={() => setMenuFor(null)} />
                        <div className="page-menu-panel" style={{ minWidth: 170 }}>
                          <button
                            type="button"
                            className="page-menu-item"
                            onClick={() => { setMenuFor(null); navigate(`/dishes/${d.id}`); }}
                          >
                            <Icon name="edit" size={15} /> פתח את המנה
                          </button>
                          <button
                            type="button"
                            className="page-menu-item"
                            onClick={() => { setMenuFor(null); setShowHelp(true); }}
                          >
                            <Icon name="info" size={15} /> איך מחושב
                          </button>
                          <button
                            type="button"
                            className="page-menu-item"
                            style={{ color: '#b91c1c' }}
                            onClick={() => { setMenuFor(null); askDelete(d); }}
                          >
                            <Icon name="trash" size={15} /> מחק את המנה
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      {/* Actions sit on the title's line, at the trailing (left) edge - the
          same shape SuppliersPage uses, and .btn-sm for the same reason: a
          page-level action is not the page's headline. As a full-width block
          below the title it was the loudest thing on a phone screen, above the
          list it exists to add to. */}
      <div className="page-title-row">
        <div className="page-title-group">
          <h1 className="page-title"><Icon name="tree" size={20} /> עץ מוצר</h1>
          {hqDishes > 0 && (
            <HqOnlyNotice
              message={`${hqDishes === 1 ? 'מנה אחת כוללת' : `${hqDishes} מנות כוללות`} רכיבים מספקים מוסתרים — מנהלים לא רואים אותם, ולכן העלות שהם רואים נמוכה יותר.`}
            />
          )}
        </div>
        <div className="page-title-actions">
          {alerts.length > 0 && (
            <button className="btn btn-sm btn-outline" onClick={() => setShowAlerts(true)}>
              <Icon name="alert" size={16} /> שינויי מחיר ({alerts.length})
            </button>
          )}
          <button className="btn btn-sm btn-primary" onClick={() => setShowAdd(true)}>+ הוסף מנה</button>
          <PageViewAsMenu />
        </div>
      </div>

      {body()}

      {/* closeOnBackdrop={false}: the form holds typed input, and a stray click
          on the backdrop must not discard it. */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="מנה חדשה" closeOnBackdrop={false}>
        <form onSubmit={handleAdd}>
          <div className="form-group">
            <label htmlFor="dish-add-name">שם המנה *</label>
            <input id="dish-add-name" className="form-control" autoFocus value={addName}
              onChange={e => setAddName(e.target.value)} placeholder="לדוגמה: סלט יווני" />
          </div>
          <div className="form-group">
            <label htmlFor="dish-add-price">מחיר מכירה בתפריט (₪)</label>
            <input id="dish-add-price" className="form-control input-money" type="number" min="0" step="0.01" inputMode="decimal"
              style={{ direction: 'ltr', textAlign: 'right' }}
              aria-describedby="dish-add-price-hint"
              value={addPrice} onChange={e => setAddPrice(e.target.value)} placeholder="-" />
            {/* Out of the placeholder: the field is ~7ch, so the sentence was
                clipped to "אוחר יותר" - read as a misspelling, not a truncation. */}
            <p className="form-hint" id="dish-add-price-hint">אפשר להזין מאוחר יותר</p>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>ביטול</button>
            <button type="submit" className="btn btn-primary" disabled={saving} aria-busy={saving}>צור מנה</button>
          </div>
        </form>
      </Modal>

      <Modal open={showAlerts} onClose={() => setShowAlerts(false)} size="lg"
        title={<><Icon name="alert" size={18} /> שינויי מחיר במנות</>} ariaLabel="שינויי מחיר במנות">
        <p style={{ fontSize: '0.85rem', color: 'var(--ink-muted)', marginBottom: 16 }}>
          מרכיבים שמחירם השתנה מאז העדכון האחרון. <DirMark dir="up">התייקר</DirMark>, <DirMark dir="down">הוזל</DirMark>.
        </p>
        <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {alerts.map(a => (
            <div key={a.id} className="dish-alert-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                <button type="button" className="cell-link" onClick={() => { setShowAlerts(false); navigate(`/dishes/${a.id}`); }}>
                  {a.name}
                </button>
                {Math.abs(a.new_cost - a.old_cost) < 0.01 ? (
                  <span className="badge badge-gray" title="המחיר של מרכיב השתנה, אך השפעתו על עלות המנה קטנה מאגורה">השפעה זניחה על המנה</span>
                ) : (
                  <span style={{ fontSize: '0.82rem', color: 'var(--ink-muted)' }}>
                    עלות מנה: מ־{formatDishCurrency(a.old_cost)} ל־<strong>{formatDishCurrency(a.new_cost)}</strong>
                    {a.new_food_cost_pct != null && <> | עלות מזון: מ־{fmtPct(a.old_food_cost_pct)} ל־{fmtPct(a.new_food_cost_pct)}</>}
                  </span>
                )}
              </div>
              {a.ingredients.map((ing, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', display: 'flex', gap: 8, padding: '2px 0', flexWrap: 'wrap' }}>
                  <DirMark dir={ing.direction}>{
                    /* diff_pct is null when the baseline was 0 (no known price until now) —
                       a percentage from a zero base is undefined, so show the reason instead. */
                    ing.diff_pct != null
                      ? `${ing.diff_pct > 0 ? '+' : ''}${ing.diff_pct.toFixed(1)}%`
                      : 'מחיר חדש'
                  }</DirMark>
                  <span>{ing.product_name} ({ing.supplier_name}): מ־{formatCurrency(ing.baseline_price)} ל־{formatCurrency(ing.current_price)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setShowAlerts(false)}>סגור</button>
          <button className="btn btn-primary" onClick={markViewed}>סמן הכל כנקרא</button>
        </div>
      </Modal>

      {/* One dialog for the whole table, opened from any row's ⋮. The columns
          are the table's, not a row's, so the copy never mentions the dish the
          menu was opened from.
          Plain words and one worked example, deliberately: the numbers here are
          a chain - a price becomes a price-per-gram, which becomes a dish cost,
          which becomes a percentage - and a definition of each link separately
          does not let anyone check the chain against their own invoice. */}
      <Modal open={showHelp} onClose={() => setShowHelp(false)} size="lg" title="איך מחושב">
        <p className="dish-help-lead">
          כל המספרים בטבלה מחושבים מהמחיר האחרון ששילמתם על המרכיבים.
        </p>

        <dl className="dish-help-list">
          <dt>מרכיבים</dt>
          <dd>כמה מוצרים נכנסים למנה. מוצר שאין לו המרה או מחיר נספר כאפס, והמנה מסומנת ״חלקי״.</dd>

          <dt>עלות (ברוטו)</dt>
          <dd>
            כמה עולים חומרי הגלם של מנה אחת - סכום העלות של כל המרכיבים.
            עלות של מרכיב היא המחיר ליחידת בסיס (גרם, מ״ל או יחידה) כפול הכמות שנכנסת למנה.
            פחת מייקר את המרכיב, כי צריך לקנות יותר כדי שיישאר מה שהמתכון דורש.
          </dd>

          <dt>מחיר מכירה</dt>
          <dd>המחיר שאתם גובים בתפריט. אתם מזינים אותו - הוא לא מחושב.</dd>

          <dt>עלות מזון %</dt>
          <dd>איזה חלק ממחיר המכירה הולך על חומרי הגלם: העלות חלקי מחיר המכירה. ככל שהאחוז נמוך יותר, נשאר לכם יותר מכל מנה שנמכרת.</dd>

          <dt>רווח גולמי</dt>
          <dd>מה שנשאר ממחיר המנה אחרי חומרי הגלם: מחיר מכירה פחות העלות. זה עוד לפני שכר, שכירות וכל שאר ההוצאות.</dd>
        </dl>

        <div className="dish-help-example">
          <div className="dish-help-example-title">דוגמה</div>
          <p>מנה שמחירה בתפריט ₪48, ונכנסים אליה 200 גרם עגבניות. ארגז עגבניות של 1,000 גרם עלה ₪10.</p>
          <ul>
            <li>מחיר לגרם: ‎10 ÷ 1,000 = <strong>₪0.01</strong></li>
            <li>עלות המנה: ‎0.01 × 200 = <strong>₪2.00</strong></li>
            <li>עלות מזון %: ‎2 ÷ 48 = <strong>4.2%</strong></li>
            <li>רווח גולמי: ‎48 − 2 = <strong>₪46.00</strong></li>
          </ul>
        </div>

        <p className="dish-help-note">
          מנה שמסומנת ״חלקי״ לא מציגה אחוז ורווח. מרכיב שלא תומחר נספר כאפס, ולכן שני המספרים היו יוצאים טובים מהמציאות.
        </p>

        <div className="modal-actions">
          <button className="btn btn-primary" onClick={() => setShowHelp(false)}>הבנתי</button>
        </div>
      </Modal>

      {confirmDialog}
    </div>
  );
}
